import React from 'react';
import * as styles from './Spinner.css';

// ================================
// TYPES ET INTERFACES
// ================================

interface SpinnerProps {
  /** Taille du spinner */
  size?: 'small' | 'medium' | 'large';
  /** Texte à afficher avec le spinner */
  text?: string;
  /** Mode d'display */
  variant?: 'inline' | 'overlay' | 'fullscreen' | 'loading' | 'modal';
  /** Couleur du spinner */
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | string;
  /** Classes CSS supplémentaires */
  className?: string;
  /** style inline personnalisé */
  style?: React.CSSProperties;
  /** Afficher/masquer le spinner */
  visible?: boolean;
  /** Animation supplémentaire */
  animation?: 'spin' | 'bounce';
  /** Positionnement centré */
  centered?: boolean;
  /** Mode responsive */
  responsive?: boolean;
  /** Accessibility - label pour les lecteurs d'écran */
  ariaLabel?: string;
}

// ================================
// UTILITAIRES
// ================================

const getColorClass = (color?: string) => {
  switch (color) {
    case 'primary':
      return styles.spinnerPrimary;
    case 'secondary':
      return styles.spinnerSecondary;
    case 'success':
      return styles.spinnerSuccess;
    case 'warning':
      return styles.spinnerWarning;
    case 'error':
      return styles.spinnerError;
    default:
      return '';
  }
};

const getSizeClass = (size?: string) => {
  switch (size) {
    case 'small':
      return styles.spinnerSmall;
    case 'large':
      return styles.spinnerLarge;
    case 'medium':
    default:
      return styles.spinnerMedium;
  }
};

const getContainerClass = (variant?: string, responsive?: boolean) => {
  let baseClass = '';

  switch (variant) {
    case 'fullscreen':
      baseClass = styles.fullScreen;
      break;
    case 'overlay':
      baseClass = styles.overlay;
      break;
    case 'loading':
      baseClass = styles.loadingContainer;
      break;
    case 'modal':
      baseClass = styles.modalSpinner;
      break;
    case 'inline':
      baseClass = styles.spinnerInline;
      break;
    default:
      baseClass = styles.spinnerContainer;
  }

  return responsive ? `${baseClass} ${styles.responsiveContainer}` : baseClass;
};

// ✅ NOUVELLE function : class de texte selon le variant
const getTextClass = (variant?: string) => {
  switch (variant) {
    case 'inline':
      return styles.spinnerTextInline;
    case 'modal':
      return styles.spinnerTextModal;
    case 'overlay':
      return styles.spinnerTextOverlay;
    case 'fullscreen':
      return styles.spinnerTextFullscreen;
    case 'loading':
      return styles.spinnerTextLoading;
    default:
      return styles.spinnerText;
  }
};

// ================================
// component PRINCIPAL
// ================================

export const Spinner: React.FC<SpinnerProps> = ({
  size = 'medium',
  text,
  variant = 'inline',
  color = 'primary',
  className = '',
  style,
  visible = true,
  animation = 'spin',
  centered = false,
  responsive = false,
  ariaLabel
}) => {
  // Si non visible, ne pas rendre
  if (!visible) {
    return null;
  }

  // construction des classes CSS
  const containerClass = getContainerClass(variant, responsive);
  const sizeClass = getSizeClass(size);
  const colorClass = getColorClass(color);
  const animationClass = animation === 'bounce' ? styles.spinnerBounce : '';
  const centeredClass = centered ? styles.spinnerCentered : '';
  const textClass = getTextClass(variant); // ✅ addition

  const spinnerClasses = [
    styles.spinnerElement,
    sizeClass,
    colorClass,
    animationClass,
    centeredClass
  ].filter(Boolean).join(' ');

  const containerClasses = [
    containerClass,
    className
  ].filter(Boolean).join(' ');

  // style personnalisé pour couleur custom
  const customStyle: React.CSSProperties = {
    ...style,
    ...(typeof color === 'string' && !['primary', 'secondary', 'success', 'warning', 'error'].includes(color)
      ? { borderTopColor: color }
      : {})
  };

  // Props d'accessibilité
  const accessibilityProps = {
    role: 'status',
    'aria-live': 'polite' as const,
    'aria-label': ariaLabel || (text ? `Chargement: ${text}` : 'Chargement en cours'),
    'aria-busy': true
  };

  return (
    <div className={containerClasses} {...accessibilityProps}>
      <div
        className={spinnerClasses}
        style={customStyle}
        data-testid="spinner-element"
      />
      {text && (
        <p
          className={textClass} // ✅ change : Utilise la class appropriée
          data-testid="spinner-text"
        >
          {text}
        </p>
      )}
    </div>
  );
};

// ================================
// COMPOSANTS PRÉDÉFINIS (inchangés)
// ================================

export const LoadingSpinner: React.FC<{
  text?: string;
  size?: 'small' | 'medium' | 'large';
}> = ({
  text = "Chargement...",
  size = 'medium'
}) => (
  <Spinner
    size={size}
    text={text}
    variant="inline"
    color="primary"
    responsive
    ariaLabel="Chargement des données"
  />
);
export const FullScreenSpinner: React.FC<{
  text?: string;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error';
}> = ({
  text = "Chargement...",
  color = 'primary'
}) => (
  <Spinner
    size="large"
    text={text}
    variant="fullscreen"
    color={color}
    ariaLabel="Chargement de l'application"
  />
);
export const OverlaySpinner: React.FC<{
  text?: string;
  visible?: boolean;
}> = ({
  text = "Traitement...",
  visible = true
}) => (
  <Spinner
    size="medium"
    text={text}
    variant="overlay"
    color="primary"
    visible={visible}
    ariaLabel="Traitement en cours"
  />
);
export const SmallSpinner: React.FC<{
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error';
}> = ({
  color = 'primary'
}) => (
  <Spinner
    size="small"
    variant="inline"
    color={color}
    ariaLabel="Chargement"
  />
);
export const ModalSpinner: React.FC<{
  text?: string;
}> = ({
  text = "Chargement..."
}) => (
  <Spinner
    size="medium"
    text={text}
    variant="modal"
    color="primary"
    ariaLabel="Chargement du contenu modal"
  />
);
export const ContentLoadingSpinner: React.FC<{
  text?: string;
  visible?: boolean;
}> = ({
  text = "Chargement du contenu...",
  visible = true
}) => (
  <Spinner
    size="large"
    text={text}
    variant="loading"
    color="primary"
    visible={visible}
    responsive
    ariaLabel="Chargement du contenu de la page"
  />
);
export const BounceSpinner: React.FC<{
  text?: string;
  size?: 'small' | 'medium' | 'large';
}> = ({
  text,
  size = 'medium'
}) => (
  <Spinner
    size={size}
    text={text}
    variant="inline"
    color="primary"
    animation="bounce"
    ariaLabel="Chargement avec animation"
  />
);
export const ButtonSpinner: React.FC = () => (
  <Spinner
    size="small"
    variant="inline"
    color="secondary"
    ariaLabel="Action en cours"
  />
);
// ================================
// HOOKS UTILITAIRES (inchangés)
// ================================

export const useSpinner = (initialState: boolean = false) => {
  const [isLoading, setIsLoading] = React.useState(initialState);

  const showSpinner = React.useCallback(() => setIsLoading(true), []);
  const hideSpinner = React.useCallback(() => setIsLoading(false), []);
  const toggleSpinner = React.useCallback(() => setIsLoading(prev => !prev), []);

  return {
    isLoading,
    showSpinner,
    hideSpinner,
    toggleSpinner,
    SpinnerComponent: React.useCallback((props: Partial<SpinnerProps>) => (
      <Spinner visible={isLoading} {...props} />
    ), [isLoading])
  };
};

export default Spinner;
